import {
  OktonPageHeader,
  OktonConfirmDialog,
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonSelect,
  OktonSearchFilter,
  OktonSegmentedFilter,
  OktonDataGrid,
  OktonEmptyState,
  ColunaGrid,
  useGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  useToast,
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { BookOpen, Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useCentrosCusto } from '../../services/hooks/useCadastros';
import { useContasBancarias } from '../../services/hooks/useContasBancarias';
import {
  EspecieLancamento,
  STATUS_CHEQUE,
  TIPO_FIN_RECEBER,
  useLancamentosFinanceiros,
} from '../../services/hooks/useLancamentosFinanceiros';
import { useDeleteLancamentoFinanceiro } from '../../services/hooks/useLancamentosFinanceirosMutations';
import { formatCustomDate } from '../../utils/formatCustomDate';
import formatValue from '../../utils/formatValue';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

/**
 * Uma linha da grade: um título do financeiro já baixado.
 *
 * A Transferência entre contas próprias saiu daqui e tem tela própria em
 * /transferencias — ela não é título, e mostrá-la junto repetia o mesmo
 * movimento em duas telas.
 */
interface LinhaLancamento {
  chave: string;
  data: string;
  descricao: string;
  classificacao: string | null;
  classificacaoCor: string;
  origem: string;
  referencia: string | null;
  // A Moeda da baixa: por qual Moeda do banco o título foi liquidado.
  moeda: string | null;
  // Já com sinal: crédito positivo, débito negativo.
  valor: number;
  conciliado: boolean;
  // Só o título do financeiro pode ser excluído por aqui.
  idFinanceiro: string | null;
  // Preenchida só no cheque: onde o papel está (Em carteira, Depositado…).
  // No movimento bancário quem responde é a coluna de conciliação.
  situacao: string | null;
}

const COLUNAS: ColunaGrid<LinhaLancamento>[] = [
  { chave: 'data', titulo: 'Data', valor: l => l.data, tipo: 'data' },
  { chave: 'descricao', titulo: 'Observação', valor: l => l.descricao },
  {
    chave: 'classificacao',
    titulo: 'Centro de Custo',
    valor: l => l.classificacao,
  },
  { chave: 'origem', titulo: 'Cliente', valor: l => l.origem },
  { chave: 'referencia', titulo: 'Referência', valor: l => l.referencia },
  { chave: 'moeda', titulo: 'Moeda Banco', valor: l => l.moeda },
  {
    chave: 'valor',
    titulo: 'Valor',
    valor: l => l.valor,
    tipo: 'numero',
    alinhamento: 'right',
  },
  {
    chave: 'conciliado',
    titulo: 'Status',
    valor: l => l.situacao ?? (l.conciliado ? 'Conciliado' : 'Pendente'),
    alinhamento: 'center',
  },
];

const LancamentosContabeis = () => {
  const history = useHistory();
  // estilos
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  // filtros
  const [filtroData, setFiltroData] = useState<FiltroData>(setaFiltroAnual());
  const [filterAccount, setFilterAccount] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  // O padrão continua sendo o movimento bancário: é o que a tela sempre
  // mostrou, e o cheque em carteira não é movimento de Conta nenhuma.
  const [especie, setEspecie] = useState<EspecieLancamento>('banco');
  const [filterStatus, setFilterStatus] = useState<
    'all' | 'pending' | 'reconciled'
  >('all');

  // dados
  const {
    data: accounts = [],
    isLoading: isAccountsLoading,
    isError: isAccountsError,
    refetch: refetchContas,
  } = useContasBancarias();
  const {
    data: transactions = [],
    isLoading: isTxLoading,
    isError: isTxError,
    refetch: refetchLancamentos,
  } = useLancamentosFinanceiros(
    filterAccount || undefined,
    'all',
    filtroData[1],
    filtroData[2],
    especie,
  );
  // Só o Centro de Custo fica: ele é filtro da grade. O resto era do
  // formulário, e foi com ele para /lancamentos/novo.
  const { data: centrosCusto = [] } = useCentrosCusto();
  const deleteMutation = useDeleteLancamentoFinanceiro();

  const toast = useToast();
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const isLoading = isAccountsLoading || isTxLoading;
  const isError = isAccountsError || isTxError;

  const recarregar = () => {
    refetchContas();
    refetchLancamentos();
  };

  const remove = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
      refetchLancamentos();
    } catch (err) {
      toast({
        title:
          err instanceof Error ? err.message : 'Erro ao excluir o lançamento.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  const filtered = useMemo(
    () =>
      // Conta e período já são aplicados no back; aqui sobram os dois filtros
      // que não custam uma ida ao servidor.
      transactions.filter(tx => {
        if (filterStatus === 'pending' && tx.conciliado_fin === '1')
          return false;
        if (filterStatus === 'reconciled' && tx.conciliado_fin !== '1')
          return false;
        // centrocusto_fin é o código do cadastro, não o nome.
        if (filterCategory && tx.centrocusto_fin !== filterCategory)
          return false;
        return true;
      }),
    [transactions, filterStatus, filterCategory],
  );

  const linhas = useMemo<LinhaLancamento[]>(
    () =>
      filtered.map(tx => ({
        chave: `fin-${tx.id_fin}`,
        data: tx.baixa_fin,
        descricao: tx.observacao_fin ?? '',
        classificacao:
          tx.ehcheque_fin === '1' ? tx.nome_ban ?? null : tx.nome_cdc,
        classificacaoCor: tx.ehcheque_fin === '1' ? 'purple' : 'blue',
        origem: tx.nome_cli ?? '',
        referencia: tx.documento_fin,
        moeda: tx.nome_moe,
        // valorbaixa_fin é sempre positivo; quem dá o sentido é tipo_fin.
        valor:
          tx.tipo_fin === TIPO_FIN_RECEBER
            ? Number(tx.valorbaixa_fin)
            : -Number(tx.valorbaixa_fin),
        conciliado: tx.conciliado_fin === '1',
        // O cheque não se exclui por aqui: ele foi criado junto com o
        // lançamento que ele liquida, e some com ele.
        idFinanceiro: tx.ehcheque_fin === '1' ? null : tx.id_fin,
        situacao:
          tx.ehcheque_fin === '1'
            ? STATUS_CHEQUE[tx.status_fin ?? ''] ?? 'Cheque'
            : null,
      })),
    [filtered],
  );

  const [busca, setBusca] = useState('');
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(linhas, COLUNAS, busca);

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="GERAL"
        title="Lançamentos Contábeis"
        subtitle="Registros internos para conciliação"
        actions={
          <OktonDateFilter
            value={{
              preset: filtroData[0],
              start: filtroData[1],
              end: filtroData[2],
            }}
            onChange={next =>
              setFiltroData([next.preset, next.start, next.end])
            }
          />
        }
      />

      <Flex w="100%" mt="4" mb="1" justifyContent="flex-end">
        <OktonSearchFilter
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por observação, cliente, referência..."
        />
      </Flex>

      <Flex minH="0" w="100%" my="3" gridGap={2} flexWrap="wrap">
        <OktonSegmentedFilter<typeof filterStatus>
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'reconciled', label: 'Conciliados' },
          ]}
        />
        <Flex
          borderWidth="1px"
          ml="auto"
          p="2"
          rounded="2xl"
          bg={bg}
          align="center"
          gridGap={2}
        >
          <Button
            leftIcon={<Plus size={18} />}
            colorScheme="blue"
            minW="195px"
            onClick={() =>
              history.push('/lancamentos/novo', { conta: filterAccount })
            }
          >
            Novo Lançamento
          </Button>
          <OktonSelect
            size="lg"
            rounded="2xl"
            name="filtroespecie"
            bg={bg}
            color={color}
            minW="225px"
            value={especie}
            onChange={e => setEspecie(e.target.value as EspecieLancamento)}
            borderWidth="2px"
          >
            <option value="banco">Lançamentos Bancários</option>
            <option value="cheque">Cheques</option>
            <option value="all">Todos</option>
          </OktonSelect>
          <OktonSearchSelect
            name="filtroconta"
            placeholder="Todas as contas"
            minWidth="200px"
            value={filterAccount}
            onChange={valor => setFilterAccount(valor)}
            options={[
              ...accounts.map(a => ({
                value: a.id_cts,
                label: a.descricao_cts,
              })),
            ]}
          />
          <OktonSearchSelect
            name="filtrocentrocusto"
            placeholder="Todos os Centros de Custo"
            minWidth="280px"
            value={filterCategory}
            onChange={valor => setFilterCategory(valor)}
            options={[
              ...centrosCusto.map(c => ({
                value: c.id_cdc,
                label: c.nome_cdc,
              })),
            ]}
          />
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={recarregar} />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={BookOpen}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhum lançamento encontrado'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  data: linha => (
                    <Box color="gray.600" whiteSpace="nowrap">
                      {/* baixa_fin é datetime: parsear o texto inteiro como
                      'yyyy-MM-dd' devolvia data inválida, e a coluna ficava
                      vazia. A hora não interessa aqui. */}
                      {formatCustomDate(
                        linha.data.slice(0, 10),
                        'yyyy-MM-dd',
                        'dd/MM/yyyy',
                      )}
                    </Box>
                  ),
                  descricao: linha => (
                    <Box color={color} fontWeight="bold" maxW="220px">
                      {linha.descricao}
                    </Box>
                  ),
                  classificacao: linha => (
                    <>
                      {linha.classificacao ? (
                        <Badge
                          colorScheme={linha.classificacaoCor}
                          borderRadius="full"
                          px="2"
                        >
                          {linha.classificacao}
                        </Badge>
                      ) : (
                        <Text color="gray.400">—</Text>
                      )}
                    </>
                  ),
                  origem: linha => (
                    <Box color="gray.500">{linha.origem || '—'}</Box>
                  ),
                  referencia: linha => (
                    <Box color="gray.500">{linha.referencia ?? '—'}</Box>
                  ),
                  moeda: linha => (
                    <Box color="gray.500">{linha.moeda ?? '—'}</Box>
                  ),
                  valor: linha => (
                    <Box
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      color={linha.valor >= 0 ? 'green.600' : 'red.500'}
                    >
                      {formatValue(linha.valor)}
                    </Box>
                  ),
                  conciliado: linha => (
                    <>
                      {linha.situacao ? (
                        <Badge colorScheme="purple" borderRadius="full" px="2">
                          {linha.situacao}
                        </Badge>
                      ) : linha.conciliado ? (
                        <Badge colorScheme="green" borderRadius="full" px="2">
                          Conciliado
                        </Badge>
                      ) : (
                        <Badge colorScheme="orange" borderRadius="full" px="2">
                          Pendente
                        </Badge>
                      )}
                    </>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: linha => (
                    <Flex justify="flex-end" gridGap="1">
                      {/* Conciliado não se exclui: o back recusa, e o botão só
                      prometeria uma ação que vai falhar. */}
                      {!!linha.idFinanceiro && !linha.conciliado && (
                        <IconButton
                          aria-label="Excluir"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<Trash2 size={16} />}
                          onClick={() =>
                            setItemExcluir(String(linha.idFinanceiro))
                          }
                        />
                      )}
                    </Flex>
                  ),
                },
              ]}
              getRowId={linha => linha.chave}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remove}
        title="Confirmar exclusão"
        description="Excluir este lançamento do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default LancamentosContabeis;
